import { Guid } from 'guid-typescript';
import { Observable, of, tap } from 'rxjs';
import { CreateDescriptionReport, CreateExpenditureReport, CreateProject, CreateContract, GetProjectsFilter, Project, Contract, ContractSummary, ProjectSummary, ReportType, UpdateDescriptionReport, UpdateExpenditureReport, UpdateProject, UpdateContract, UpdateProjectBudget, ChangeProjectBudgetStatus, ChangeDescriptionReportStatus, ChangeExpenditureReportStatus, ChangeProjectStatus, RefundSummary, Refund, CreateRefund, UpdateRefund, ProjectSaldo, ProjectBudget, DescriptionReport, ExpenditureReport, CreateProjectBudgetReallocation } from '../models/project';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {

  private baseUrl = `${environment.apiUrl}/projects`;

  private projectCache: Map<string, Project> = new Map();

  constructor(private http: HttpClient) {}

  getProjects(filter: GetProjectsFilter): Observable<ProjectSummary[]> {
    const params = filter.toQueryParams();

    return this.http.get<ProjectSummary[]>(this.baseUrl, { params });
  }

  getProject(id: Guid): Observable<Project> {
    const cacheKey = id.toString();
    if (this.projectCache.has(cacheKey)) {
      return of(this.projectCache.get(cacheKey)!);
    }

    return this.http.get<Project>(`${this.baseUrl}/${id}`).pipe(
      tap(project => {
        this.projectCache.set(cacheKey, project);
      })
    );
  }

  createProject(createDto: CreateProject): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}`, createDto);
  }

  updateProject(id: Guid, updateDto: UpdateProject): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}`, updateDto).pipe(
      tap(() => this.invalidateProjectCache(id))
    );
  }

  changeProjectStatus(projectId: Guid,changeStatusDto: ChangeProjectStatus): Observable<void> {
    const url = `${this.baseUrl}/${projectId}/status`;
    return this.http.put<void>(url, changeStatusDto).pipe(
       tap(() => {
        this.invalidateProjectCache(projectId);
      })
    );
  }

  downloadProjectBudget(projectId: string, projectBudgetId: string): Observable<Blob> {
    const url = `${this.baseUrl}/${projectId}/budgets/${projectBudgetId}/form`;
    return this.http.get(url, { responseType: 'blob' });
  }

  createProjectBudgetReallocation(projectId: Guid, createProjectBudgetReallocationDto: CreateProjectBudgetReallocation): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${projectId}/budgets`, createProjectBudgetReallocationDto).pipe(
      tap(() => {
        this.invalidateProjectCache(projectId);
      })
    );
  }

  getProjectBudgets(projectId: Guid): Observable<ProjectBudget[]> {
    return this.http.get<ProjectBudget[]>(`${this.baseUrl}/${projectId}/budgets`);
  }

  getProjectBudget(projectId: Guid, budgetId: Guid): Observable<ProjectBudget> {
    return this.http.get<ProjectBudget>(`${this.baseUrl}/${projectId}/budgets/${budgetId}`);
  }

  updateProjectBudget(projectId: Guid, projectBudgetId: Guid, updateDto: UpdateProjectBudget): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${projectId}/budgets/${projectBudgetId}`, updateDto).pipe(
      tap(() => {
        this.invalidateProjectCache(projectId);
      })
    );
  }

  changeProjectBudgetStatus(projectId: Guid, projectBudgetId: Guid, changeStatusDto: ChangeProjectBudgetStatus): Observable<void> {
    const url = `${this.baseUrl}/${projectId}/budgets/${projectBudgetId}/status`;
    return this.http.put<void>(url, changeStatusDto).pipe(
       tap(() => {
        this.invalidateProjectCache(projectId);
      })
    );
  }

  getDescriptionReports(projectId: Guid): Observable<DescriptionReport[]> {
    return this.http.get<DescriptionReport[]>(`${this.baseUrl}/${projectId}/reports/description`);
  }

  getDescriptionReport(projectId: Guid, reportId: Guid): Observable<DescriptionReport> {
    return this.http.get<DescriptionReport>(`${this.baseUrl}/${projectId}/reports/description/${reportId}`);
  }

  createDescriptionReport(projectId: Guid, createDto: CreateDescriptionReport): Observable<string> {
    return this.http.post<string>(`${this.baseUrl}/${projectId}/reports/description`, createDto).pipe(
      tap(() => {
        this.invalidateProjectCache(projectId);
      })
    );
  }

  updateDescriptionReport(projectId: Guid, reportId: Guid, updateDto: UpdateDescriptionReport): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${projectId}/reports/description/${reportId}`, updateDto).pipe(
      tap(() => {
        this.invalidateProjectCache(projectId);
      })
    );
  }

  deleteDescriptionReport(projectId: Guid, reportId: Guid): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${projectId}/reports/description/${reportId}`).pipe(
      tap(() => {
        this.invalidateProjectCache(projectId);
      })
    );
  }

  getExpenditureReports(projectId: Guid): Observable<ExpenditureReport[]> {
    return this.http.get<ExpenditureReport[]>(`${this.baseUrl}/${projectId}/reports/expenditure`);
  }

  getExpenditureReport(projectId: Guid, reportId: Guid): Observable<ExpenditureReport> {
    return this.http.get<ExpenditureReport>(`${this.baseUrl}/${projectId}/reports/expenditure/${reportId}`);
  }

  createExpenditureReport(projectId: Guid, createDto: CreateExpenditureReport): Observable<any> {
    return this.http.post(`${this.baseUrl}/${projectId}/reports/expenditure`, createDto, { observe: 'response' }).pipe(
      tap(() => {
        this.invalidateProjectCache(projectId);
      })
    );
  }

  updateExpenditureReport(projectId: Guid, reportId: Guid, updateDto: UpdateExpenditureReport): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${projectId}/reports/expenditure/${reportId}`, updateDto).pipe(
      tap(() => {
        this.invalidateProjectCache(projectId);
      })
    );
  }

  changeProjectReportStatus(projectId: Guid, reportId: Guid, reportType: ReportType, changeStatusDto: ChangeExpenditureReportStatus): Observable<void> {
    const url = `${this.baseUrl}/${projectId}/reports/${reportId}/type/${reportType}/status`;
    return this.http.put<void>(url, changeStatusDto).pipe(
       tap(() => {
        this.invalidateProjectCache(projectId);
      })
    );
  }

  deleteExpenditureReport(projectId: Guid, reportId: Guid): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${projectId}/reports/expenditure/${reportId}`).pipe(
      tap(() => {
        this.invalidateProjectCache(projectId);
      })
    );
  }

  private invalidateProjectCache(projectId: Guid): void {
    this.projectCache.delete(projectId.toString());
  }

  //For these methods there is no need to invalidate cache because they are separate endpoints. Refunds and Contracts are not coming over the wire as part of project aggregate.

  getProjectSaldo(projectId: string): Observable<ProjectSaldo> {
    return this.http.get<ProjectSaldo>(`${this.baseUrl}/${projectId}/saldo`);
  }


  getContracts(projectId: string): Observable<ContractSummary[]> {
    return this.http.get<ContractSummary[]>(`${this.baseUrl}/${projectId}/contracts`);
  }

  getContract(projectId: string, contractId: string): Observable<Contract> {
    return this.http.get<Contract>(`${this.baseUrl}/${projectId}/contracts/${contractId}`);
  }

  createContract(projectId: string, createDto: CreateContract): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${projectId}/contracts`, createDto);
  }

  updateContract(projectId: string, contractId: string, updateDto: UpdateContract): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${projectId}/contracts/${contractId}`, updateDto);
  }

  deleteContract(projectId: string, contractId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${projectId}/contracts/${contractId}`);
  }

  downloadContract(projectId: string, contractId: string): Observable<Blob> {
    const url = `${this.baseUrl}/${projectId}/contracts/${contractId}/form`;
    return this.http.get(url, { responseType: 'blob' });
  }

  
  getRefunds(projectId: string): Observable<RefundSummary[]> {
    return this.http.get<RefundSummary[]>(`${this.baseUrl}/${projectId}/refunds`);
  }

  getRefund(projectId: string, refundId: string): Observable<Refund> {
    return this.http.get<Refund>(`${this.baseUrl}/${projectId}/refunds/${refundId}`);
  }

  createRefund(projectId: string, createDto: CreateRefund): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${projectId}/refunds`, createDto);
  }

  updateRefund(projectId: string, refundId: string, updateDto: UpdateRefund): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${projectId}/refunds/${refundId}`, updateDto);
  }

  deleteRefund(projectId: string, refundId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${projectId}/refunds/${refundId}`);
  }

  downloadRefund(projectId: string, refundId: string): Observable<Blob> {
    const url = `${this.baseUrl}/${projectId}/refunds/${refundId}/form`;
    return this.http.get(url, { responseType: 'blob' });
  }


  downloadReport(projectId: Guid, reportId: Guid, reportType: ReportType): Observable<Blob> {
    const url = `${this.baseUrl}/${projectId}/reports/${reportId}/type/${reportType}`;
    return this.http.get(url, { responseType: 'blob' });
  }
}